import { useEffect, useMemo, useState } from 'react';
import { api, fetchCached } from '@/lib/api';
import { FALLBACK_ACTIVITIES_CONTENT } from '@/lib/publicPageData';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { CalendarDays, MapPin, Trophy, Utensils, Heart, Baby, Users, BookOpen, Music, ArrowRight, CheckCircle, Send, Sparkles } from 'lucide-react';

const ICON_MAP = {
  dinner: Utensils,
  banquet: Heart,
  camp: Baby,
  soccer: Trophy,
  youth: Users,
  bible: BookOpen,
  music: Music,
};

const EMPTY_FORM = {
  full_name: '',
  email: '',
  phone: '',
  church_organization: '',
  participants_count: 1,
  message: '',
  participant_group_name: '',
  city_country: '',
  category: '',
  contact_person: '',
  website: '',
};

const normalizeActivityName = (value = '') =>
  value.toLowerCase().replace(/[’‘]/g, "'").normalize('NFD').replace(/[\u0300-\u036f]/g, '');

export default function ActivitiesPage() {
  const [content, setContent] = useState(FALLBACK_ACTIVITIES_CONTENT);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    fetchCached('/content/activities').then((data) => {
      setContent({
        ...FALLBACK_ACTIVITIES_CONTENT,
        ...data,
        activities: data?.activities?.length ? data.activities : FALLBACK_ACTIVITIES_CONTENT.activities,
      });
    }).catch(() => {});
  }, []);

  const featuredActivity = content.activities?.[content.activities.length - 1];
  const primaryActivities = content.activities?.slice(0, -1) || [];
  const isKonkou = useMemo(
    () => normalizeActivityName(selectedActivity?.title || '').includes('konkou chante nwel'),
    [selectedActivity]
  );

  const openRegistration = (activity) => {
    setSelectedActivity(activity);
    setForm(EMPTY_FORM);
    setSubmitError('');
    setSuccessMessage('');
  };

  const closeRegistration = (open) => {
    if (open) return;
    setSelectedActivity(null);
    setSubmitError('');
    setSuccessMessage('');
    setForm(EMPTY_FORM);
  };

  const updateForm = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const submitRegistration = async (event) => {
    event.preventDefault();
    if (!selectedActivity) return;
    setSubmitting(true);
    setSubmitError('');

    try {
      await api.post('/activity-registrations', {
        selected_activity_name: selectedActivity.title,
        activity_date: selectedActivity.date,
        ...form,
        participants_count: Number(form.participants_count || 1),
      });
      setSuccessMessage(`Thank you. Your registration for ${selectedActivity.title} has been received.`);
      toast.success('Registration received');
      setForm(EMPTY_FORM);
    } catch (error) {
      const message = error?.response?.data?.detail || 'Registration could not be sent. Please try again.';
      setSubmitError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const ActivityCard = ({ activity, index, featured = false }) => {
    const Icon = ICON_MAP[activity.icon] || CalendarDays;
    return (
      <article
        data-testid={`activity-card-${index + 1}`}
        className={`group mscm-activity-card h-full ${featured ? 'mscm-activity-card-featured' : 'mscm-activity-card-list'}`}
      >
        <button
          type="button"
          onClick={() => openRegistration(activity)}
          className={`block h-full w-full text-left ${featured ? 'p-6 md:p-7' : 'p-5 md:p-6'}`}
        >
          <div className="flex items-start gap-5">
            <div className={`mscm-date-badge flex h-[4.5rem] w-[4.5rem] shrink-0 flex-col items-center justify-center rounded-[8px] text-[#1E4FA1] ${featured ? 'mscm-date-badge-featured' : ''}`}>
              <span className="text-[11px] font-black uppercase tracking-[0.14em]">{activity.month}</span>
              <span className="text-lg font-black leading-none">{activity.day || '2027'}</span>
            </div>
            <div className="min-w-0 flex-1">
              <div className="mb-4 flex items-center justify-between gap-3">
                <span className={`mscm-activity-index ${featured ? 'text-white/70' : ''}`}>
                  Activity {String(index + 1).padStart(2, '0')}
                </span>
                <div className={`mscm-activity-icon inline-flex h-10 w-10 items-center justify-center rounded-[8px] ${featured ? 'mscm-activity-icon-featured text-white' : 'text-[#1E4FA1]'}`}>
                  <Icon size={20} />
                </div>
              </div>
              <h3 className={`mscm-text-safe text-xl font-bold leading-tight md:text-2xl ${featured ? 'text-white' : 'text-[#0F172A]'}`} style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                {activity.title}
              </h3>
              <p className={`mscm-text-safe mt-3 text-sm font-semibold ${featured ? 'text-white/80' : 'text-[#1E4FA1]'}`}>{activity.date}</p>
              {activity.detail ? (
                <p className={`mscm-text-safe mt-2 text-sm leading-relaxed ${featured ? 'text-white/70' : 'text-[#526274]'}`}>{activity.detail}</p>
              ) : null}
              <span className={`mt-5 inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold transition-all duration-200 ${featured ? 'bg-white text-[#1E4FA1] group-hover:shadow-[0_14px_30px_rgba(0,0,0,0.18)]' : 'bg-[#1E4FA1] text-white shadow-[0_14px_30px_rgba(30,79,161,0.22)] group-hover:bg-[#0B1F4D]'}`}>
                Register <ArrowRight size={15} />
              </span>
            </div>
          </div>
        </button>
      </article>
    );
  };

  return (
    <div data-testid="activities-page" className="pt-20 mscm-public-shell overflow-x-hidden">
      <section className="mscm-hero-panel mscm-cinematic-hero py-20 md:py-28">
        <div className="mscm-hero-aurora" />
        <div className="mscm-star-field" />
        <div className="mscm-luminous-sweep" />
        <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-8">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_360px] lg:items-end">
            <div className="mscm-hero-copy">
              <span className="inline-flex max-w-full items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-white/80 backdrop-blur-md">
                <Sparkles size={14} />
                <span className="min-w-0 truncate">Ministry calendar</span>
              </span>
              <h1 className="mscm-hero-title mt-5 font-bold text-white lg:text-6xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                {content.title}
              </h1>
              <p className="mt-5 text-white/80 text-base md:text-lg max-w-2xl leading-relaxed">
                Choose an activity and sign up. Your registration will be sent privately to the ministry team.
              </p>
            </div>
            <div className="grid grid-cols-3 gap-3 lg:grid-cols-1">
              <div className="mscm-hero-metric p-4">
                <p className="text-2xl font-black text-white">{content.activities?.length || 0}</p>
                <p className="mt-1 text-[11px] font-bold uppercase tracking-[0.14em] text-white/60">Events</p>
              </div>
              <div className="mscm-hero-metric p-4">
                <p className="text-2xl font-black text-white">2027</p>
                <p className="mt-1 text-[11px] font-bold uppercase tracking-[0.14em] text-white/60">Calendar</p>
              </div>
              <div className="mscm-hero-metric p-4">
                <p className="mscm-text-safe text-2xl font-black text-white">Secure</p>
                <p className="mt-1 text-[11px] font-bold uppercase tracking-[0.14em] text-white/60">Signup</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        id="activities-list"
        className="relative isolate overflow-hidden mscm-section mscm-section-orbit mscm-diagonal-section"
      >
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-8">
          <div className="mb-14 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div className="mscm-section-intro">
              <span className="mscm-eyebrow">Annual Activities</span>
              <h2 className="mscm-section-title mscm-display mt-3 font-bold md:text-5xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Register for 2027 Events
              </h2>
            </div>
            <p className="max-w-md text-sm leading-relaxed text-[#526274]">
              Each activity opens the registration form for that specific event.
            </p>
          </div>

          <div className="mscm-activity-grid grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
            {primaryActivities.map((activity, index) => (
              <ActivityCard key={`${activity.title}-${activity.month}`} activity={activity} index={index} />
            ))}
          </div>
        </div>
      </section>

      {featuredActivity ? (
        <section className="mscm-section mscm-futuristic-band">
          <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.15fr] gap-8 items-stretch">
              <div>
                <span className="text-white/70 text-xs font-bold uppercase tracking-[0.16em]">Featured Finale</span>
                <h2 className="mscm-section-title mt-4 font-bold text-white md:text-5xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  {featuredActivity.title}
                </h2>
                <p className="mt-5 text-white/75 leading-relaxed">
                  Registration opens early in the year and leads toward a December final in Tampa, Florida.
                </p>
                <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="mscm-hero-metric p-5">
                    <CalendarDays size={22} className="text-white mb-4" />
                    <p className="text-xs font-bold uppercase tracking-[0.14em] text-white/60">Registration</p>
                    <p className="mt-2 text-white font-semibold">{featuredActivity.registration_period}</p>
                  </div>
                  <div className="mscm-hero-metric p-5">
                    <Trophy size={22} className="text-white mb-4" />
                    <p className="text-xs font-bold uppercase tracking-[0.14em] text-white/60">Big Final</p>
                    <p className="mt-2 text-white font-semibold">{featuredActivity.big_final}</p>
                  </div>
                  <div className="mscm-hero-metric p-5">
                    <MapPin size={22} className="text-white mb-4" />
                    <p className="text-xs font-bold uppercase tracking-[0.14em] text-white/60">Location</p>
                    <p className="mt-2 text-white font-semibold">{featuredActivity.location}</p>
                  </div>
                </div>
              </div>
              <ActivityCard activity={featuredActivity} index={primaryActivities.length} featured />
            </div>
          </div>
        </section>
      ) : null}

      <Dialog open={!!selectedActivity} onOpenChange={closeRegistration}>
        <DialogContent className="mscm-registration-modal max-h-[calc(100dvh_-_2rem)] overflow-y-auto rounded-[8px] border-[#DDEBFA] bg-white/95 p-0 shadow-[0_30px_90px_rgba(11,31,77,0.22)] backdrop-blur-xl sm:max-w-2xl">
          <div className="bg-[linear-gradient(135deg,#F7FBFF,#EAF4FF)] px-6 py-5 border-b border-[#DDEBFA]">
            <DialogHeader>
              <DialogTitle className="text-2xl text-[#0F172A]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Activity Registration
              </DialogTitle>
            </DialogHeader>
            {selectedActivity ? (
              <div className="mt-4 rounded-[8px] border border-[#DDEBFA] bg-white/80 p-4 shadow-[0_12px_34px_rgba(11,31,77,0.07)]">
                <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#1E4FA1]">Selected activity</p>
                <p data-testid="registration-selected-activity" className="mscm-text-safe mt-1 font-semibold text-[#0F172A]">{selectedActivity.title}</p>
                <p className="mscm-text-safe mt-1 text-sm text-[#526274]">{selectedActivity.date}</p>
              </div>
            ) : null}
          </div>

          <div className="p-6">
            {successMessage ? (
              <div data-testid="activity-registration-success" className="rounded-[8px] bg-[linear-gradient(135deg,#EAF4FF,#FFFFFF)] p-5 text-center shadow-[inset_0_0_0_1px_rgba(30,79,161,0.08)] sm:p-8">
                <CheckCircle size={48} className="mx-auto mb-4 text-[#10B981]" />
                <h3 className="text-xl font-bold text-[#0F172A]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  Registration Sent
                </h3>
                <p className="mt-2 text-[#526274]">{successMessage}</p>
                <Button onClick={() => closeRegistration(false)} size="lg" className="mt-5">
                  Close
                </Button>
              </div>
            ) : (
              <form data-testid="activity-registration-form" onSubmit={submitRegistration} className="space-y-5">
                <input
                  type="text"
                  name="website"
                  autoComplete="off"
                  tabIndex={-1}
                  value={form.website}
                  onChange={(event) => updateForm('website', event.target.value)}
                  className="hidden"
                  aria-hidden="true"
                />

                {submitError ? (
                  <div className="rounded-[8px] border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {submitError}
                  </div>
                ) : null}

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field label="Full name">
                    <Input value={form.full_name} onChange={(event) => updateForm('full_name', event.target.value)} required maxLength={120} className="mscm-input-focus h-11 rounded-[8px] border-[#DDEBFA]" />
                  </Field>
                  <Field label="Email">
                    <Input type="email" value={form.email} onChange={(event) => updateForm('email', event.target.value)} required maxLength={254} className="mscm-input-focus h-11 rounded-[8px] border-[#DDEBFA]" />
                  </Field>
                  <Field label="Phone number">
                    <Input value={form.phone} onChange={(event) => updateForm('phone', event.target.value)} required maxLength={40} className="mscm-input-focus h-11 rounded-[8px] border-[#DDEBFA]" />
                  </Field>
                  <Field label="Number of participants">
                    <Input type="number" min="1" max="1000" value={form.participants_count} onChange={(event) => updateForm('participants_count', event.target.value)} required className="mscm-input-focus h-11 rounded-[8px] border-[#DDEBFA]" />
                  </Field>
                </div>

                <Field label="Church / organization name">
                  <Input value={form.church_organization} onChange={(event) => updateForm('church_organization', event.target.value)} required maxLength={160} className="mscm-input-focus h-11 rounded-[8px] border-[#DDEBFA]" />
                </Field>

                {isKonkou ? (
                  <div className="grid grid-cols-1 gap-4 rounded-[8px] border border-[#DDEBFA] bg-[#F7FBFF] p-4 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.7)] sm:grid-cols-2">
                    <Field label="Participant/group name">
                      <Input value={form.participant_group_name} onChange={(event) => updateForm('participant_group_name', event.target.value)} maxLength={160} className="mscm-input-focus h-11 rounded-[8px] border-[#DDEBFA]" />
                    </Field>
                    <Field label="City/country">
                      <Input value={form.city_country} onChange={(event) => updateForm('city_country', event.target.value)} maxLength={120} className="mscm-input-focus h-11 rounded-[8px] border-[#DDEBFA]" />
                    </Field>
                    <Field label="Category">
                      <Input value={form.category} onChange={(event) => updateForm('category', event.target.value)} placeholder="Solo, group, choir..." maxLength={80} className="mscm-input-focus h-11 rounded-[8px] border-[#DDEBFA]" />
                    </Field>
                    <Field label="Contact person">
                      <Input value={form.contact_person} onChange={(event) => updateForm('contact_person', event.target.value)} maxLength={120} className="mscm-input-focus h-11 rounded-[8px] border-[#DDEBFA]" />
                    </Field>
                  </div>
                ) : null}

                <Field label="Message / notes">
                  <Textarea value={form.message} onChange={(event) => updateForm('message', event.target.value)} rows={4} maxLength={3000} className="mscm-input-focus rounded-[8px] border-[#DDEBFA]" />
                </Field>

                <Button type="submit" disabled={submitting} data-testid="activity-registration-submit" size="lg" className="min-h-11 w-full sm:w-auto">
                  {submitting ? 'Sending...' : <span className="flex items-center gap-2">Submit Registration <Send size={16} /></span>}
                </Button>
              </form>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-medium text-[#0F172A]">{label}</span>
      {children}
    </label>
  );
}
